import React, { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import {
  getDatabase, ref, onValue, update, remove, push
} from 'firebase/database';
import {
  Box, Typography, Paper, Grid, Switch, FormControlLabel, TextField,
  InputAdornment, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Snackbar, Alert,
  MenuItem, Select, FormControl, InputLabel, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaceIcon from '@mui/icons-material/Place';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { MAIN_COLOR, FONT_FAMILY } from '../common/sharedFunctions';

// Replicates mobile placeNormalize.js normalization + covers all name/neighborhood fields.
// Tokens from AR + FR + EN + neighborhood ensure cross-language search works when
// the admin writes place names in multiple languages.
const PLACE_STOP_WORDS = new Set([
  'a', 'à', 'de', 'du', 'la', 'le', 'les', 'el', 'al', 'the', 'of', 'in',
  'near', 'près', 'qrb', 'قرب', 'في', 'من', 'إلى', 'ila',
]);

function normAr(s) {
  if (!s) return '';
  return s
    .replace(/[ً-ٰٟ]/g, '')           // strip tashkeel
    .replace(/[أإآ]/g, 'ا')            // normalize alef variants
    .replace(/ى/g, 'ي')                // ى → ي
    .replace(/ة/g, 'ه')                // ة → ه (Hassaniya normalization)
    .replace(/[,.\-_/\\'"«»()[\]{};:!?،]/g, ' ')
    .replace(/\s+/g, ' ').trim().toLowerCase();
}

function normFrEn(s) {
  if (!s) return '';
  return s
    .normalize('NFD').replace(/[̀-ͯ]/g, '').normalize('NFC') // strip accents
    .toLowerCase()
    .replace(/[,.\-_/\\'"«»()[\]{};:!?]/g, ' ')
    .replace(/\s+/g, ' ').trim();
}

function placeTokenize(s) {
  return s.split(/\s+/).filter(t => t.length >= 2 && !PLACE_STOP_WORDS.has(t));
}

function buildPlaceTokens({ nameFr, nameAr, nameEn, neighborhoodFr, neighborhoodAr }) {
  const all = [
    ...placeTokenize(normFrEn(nameFr || '')),
    ...placeTokenize(normAr(nameAr || '')),
    ...placeTokenize(normFrEn(nameEn || '')),
    ...placeTokenize(normFrEn(neighborhoodFr || '')),
    ...placeTokenize(normAr(neighborhoodAr || '')),
  ];
  return [...new Set(all)];
}

const useStyles = makeStyles({
  root: { padding: 24 },
  title: { fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: 22, marginBottom: 4, color: MAIN_COLOR },
  sectionTitle: { fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: 15, marginBottom: 8, marginTop: 16 },
  statCard: { padding: 16, borderRadius: 10, textAlign: 'center', marginBottom: 8 },
  statValue: { fontFamily: FONT_FAMILY, fontSize: 22, fontWeight: 700, color: MAIN_COLOR },
  statLabel: { fontFamily: FONT_FAMILY, fontSize: 12, color: '#777', marginTop: 2 },
  tableHead: { backgroundColor: MAIN_COLOR },
  tableHeadCell: { color: '#fff', fontFamily: FONT_FAMILY, fontWeight: 600 },
  filterRow: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
});

const PAGE_SIZE = 50;

function parseGpsSearch(str) {
  const m = str.trim().match(/^(-?\d{1,3}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = parseFloat(m[1]);
  const lng = parseFloat(m[2]);
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

function mapsUrl(lat, lng) {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

const SOURCE_LABELS = {
  seed_google_maps: 'Seed (Google Maps)',
  booking_pickup: 'Booking pickup',
  booking_drop: 'Booking drop',
  backfill_historical: 'Backfill historical',
  user_pin: 'User pin',
  google_autocomplete: 'Google autocomplete',
  callcenter_human: 'Call center human',
  admin_manual: 'Admin manual',
};

export default function SawiPlacesManager() {
  const classes = useStyles();
  const db = getDatabase();

  const [hookEnabled, setHookEnabled] = useState(false);
  const [hookLoading, setHookLoading] = useState(true);
  const [places, setPlaces] = useState([]);
  const [filteredPlaces, setFilteredPlaces] = useState([]);
  const [stats, setStats] = useState({ total: 0, bySrc: {} });
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editData, setEditData] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'info' });
  const [deactivateDialog, setDeactivateDialog] = useState(false);
  const [dupConfirmDialog, setDupConfirmDialog] = useState({ open: false, dupName: '' });
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = newest first
  const [backfilling, setBackfilling] = useState(false);
  const [backfillDone, setBackfillDone] = useState(0);

  // Google search panel state
  const [gSearch, setGSearch] = useState('');
  const [gSuggestions, setGSuggestions] = useState([]);
  const [gSelected, setGSelected] = useState(null);
  const [gFormData, setGFormData] = useState({ nameFr: '', nameAr: '', lat: '', lng: '', phone: '', neighborhoodFr: '', neighborhoodAr: '', category: '' });
  const [gSaving, setGSaving] = useState(false);
  const autocompleteRef = useRef(null);
  const placesServiceRef = useRef(null);
  const placesNodeRef = useRef(null);

  // Feature flag listener
  useEffect(() => {
    const flagRef = ref(db, 'featureFlags/savePlacesHook/enabled');
    const unsubscribe = onValue(flagRef, snap => {
      setHookEnabled(!!snap.val());
      setHookLoading(false);
    });
    return () => unsubscribe();
  }, [db]);

  // Load all places from RTDB (flat list across all customers)
  useEffect(() => {
    setLoading(true);
    const placesRef = ref(db, 'sawiPlaces');
    const unsubscribe = onValue(placesRef, snap => {
      if (!snap.exists()) { setPlaces([]); setStats({ total: 0, bySrc: {} }); setLoading(false); return; }
      const arr = [];
      const bySrc = {};
      snap.forEach(child => {
        const val = child.val();
        arr.push({ id: child.key, ...val });
        const src = val.source || 'unknown';
        bySrc[src] = (bySrc[src] || 0) + 1;
      });
      setPlaces(arr);
      setStats({ total: arr.length, bySrc });
      setLoading(false);
    });
    return () => unsubscribe();
  }, [db]);

  // Filter + search
  useEffect(() => {
    let result = places;
    if (filterSource) result = result.filter(p => p.source === filterSource);
    if (filterCategory) result = result.filter(p => p.category === filterCategory);
    if (search.trim()) {
      const gps = parseGpsSearch(search);
      if (gps) {
        const RADIUS = 0.005; // ~500 m for search
        result = result.filter(p =>
          p.geo &&
          Math.abs((p.geo.lat || 0) - gps.lat) < RADIUS &&
          Math.abs((p.geo.lng || 0) - gps.lng) < RADIUS
        );
      } else {
        const q = search.trim().toLowerCase();
        result = result.filter(p => {
          const nbhd = typeof p.neighborhood === 'string' ? p.neighborhood : (p.neighborhood?.fr || p.neighborhood?.ar || '');
          const lbl = p.labels?.fr || p.labels?.ar || p.labels?.en || '';
          const label = [lbl, p.address, nbhd].filter(Boolean).join(' ').toLowerCase();
          return label.includes(q);
        });
      }
    }
    setFilteredPlaces(result);
    setPage(0);
  }, [places, search, filterSource, filterCategory]);

  const handleToggle = async () => {
    if (hookEnabled) {
      // Turning OFF: show deactivate flow dialog
      setDeactivateDialog(true);
    } else {
      await update(ref(db, 'featureFlags/savePlacesHook'), { enabled: true });
      setToast({ open: true, msg: 'Place saving enabled.', severity: 'success' });
    }
  };

  const handleDeactivateConfirm = async () => {
    setSubmitting(true);
    try {
      await update(ref(db, 'featureFlags/savePlacesHook'), { enabled: false });
      setToast({ open: true, msg: 'Place saving disabled.', severity: 'info' });
    } finally {
      setSubmitting(false);
      setDeactivateDialog(false);
    }
  };

  const handleEditOpen = (place) => {
    setEditTarget(place);
    setEditData({
      'labels/fr': place.labels?.fr || '',
      'labels/ar': place.labels?.ar || '',
      'labels/en': place.labels?.en || '',
      category: place.category || '',
      phone: place.phone || '',
      'neighborhood/fr': typeof place.neighborhood === 'object' ? (place.neighborhood?.fr || '') : '',
      'neighborhood/ar': typeof place.neighborhood === 'string' ? place.neighborhood : (place.neighborhood?.ar || ''),
      'geo/lat': place.geo?.lat ?? '',
      'geo/lng': place.geo?.lng ?? '',
    });
    setEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setSubmitting(true);
    try {
      const payload = { ...editData };
      if (payload['geo/lat'] !== '' && payload['geo/lat'] !== undefined) payload['geo/lat'] = parseFloat(payload['geo/lat']);
      if (payload['geo/lng'] !== '' && payload['geo/lng'] !== undefined) payload['geo/lng'] = parseFloat(payload['geo/lng']);
      // Rebuild searchTokens from all name + neighborhood fields so edits are immediately searchable
      payload['searchTokens'] = buildPlaceTokens({
        nameFr: payload['labels/fr'] || editTarget.labels?.fr || '',
        nameAr: payload['labels/ar'] || editTarget.labels?.ar || '',
        nameEn: payload['labels/en'] || editTarget.labels?.en || '',
        neighborhoodFr: payload['neighborhood/fr'] || (typeof editTarget.neighborhood === 'object' ? editTarget.neighborhood?.fr : '') || '',
        neighborhoodAr: payload['neighborhood/ar'] || (typeof editTarget.neighborhood === 'object' ? editTarget.neighborhood?.ar : editTarget.neighborhood) || '',
      });
      await update(ref(db, `sawiPlaces/${editTarget.id}`), payload);
      await update(ref(db, 'sawiPlaces_meta'), { last_modified: Date.now() });
      setToast({ open: true, msg: 'Place updated.', severity: 'success' });
      setEditDialog(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOpen = (place) => {
    setDeleteTarget(place);
    setDeleteDialog(true);
  };

  const handleBackfillUrls = async () => {
    const toUpdate = places.filter(p => p.geo?.lat && p.geo?.lng && !p.googleMapsUrl);
    if (!toUpdate.length) {
      setToast({ open: true, msg: 'All places already have a Google Maps URL.', severity: 'success' });
      return;
    }
    setBackfilling(true);
    setBackfillDone(0);
    let done = 0;
    const BATCH = 50;
    try {
      for (let i = 0; i < toUpdate.length; i += BATCH) {
        const batch = toUpdate.slice(i, i + BATCH);
        await Promise.all(batch.map(p =>
          update(ref(db, `sawiPlaces/${p.id}`), { googleMapsUrl: mapsUrl(p.geo.lat, p.geo.lng) })
        ));
        done += batch.length;
        setBackfillDone(done);
      }
      setToast({ open: true, msg: `Google Maps URL added to ${done} places.`, severity: 'success' });
    } catch (e) {
      setToast({ open: true, msg: `Backfill error: ${e.message}`, severity: 'error' });
    } finally {
      setBackfilling(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await remove(ref(db, `sawiPlaces/${deleteTarget.id}`));
      setToast({ open: true, msg: 'Place deleted.', severity: 'success' });
      setDeleteDialog(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Google search handlers
  const handleGoogleSearch = useCallback((value) => {
    if (!value || value.length < 3) { setGSuggestions([]); return; }
    if (!window.google?.maps?.places) { return; }
    if (!autocompleteRef.current) {
      autocompleteRef.current = new window.google.maps.places.AutocompleteService();
    }
    autocompleteRef.current.getPlacePredictions(
      { input: value, componentRestrictions: { country: 'mr' }, language: 'fr' },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setGSuggestions(predictions.map(p => ({ place_id: p.place_id, description: p.description })));
        } else {
          setGSuggestions([]);
        }
      }
    );
  }, []);

  useEffect(() => {
    const t = setTimeout(() => handleGoogleSearch(gSearch), 400);
    return () => clearTimeout(t);
  }, [gSearch, handleGoogleSearch]);

  const handleGoogleSelect = (suggestion) => {
    setGSuggestions([]);
    if (!window.google?.maps?.places || !placesNodeRef.current) return;
    if (!placesServiceRef.current) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(placesNodeRef.current);
    }
    placesServiceRef.current.getDetails(
      { placeId: suggestion.place_id, fields: ['name', 'geometry', 'formatted_phone_number', 'international_phone_number'] },
      (result, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !result) return;
        const lat = result.geometry?.location?.lat();
        const lng = result.geometry?.location?.lng();
        setGSelected({ placeId: suggestion.place_id });
        setGFormData({
          nameFr: result.name || '',
          nameAr: '',
          lat: lat ? String(Math.round(lat * 1000000) / 1000000) : '',
          lng: lng ? String(Math.round(lng * 1000000) / 1000000) : '',
          phone: result.international_phone_number || result.formatted_phone_number || '',
          neighborhoodFr: '',
          neighborhoodAr: '',
          category: '',
        });
      }
    );
  };

  const doSaveNewPlace = async () => {
    const lat = parseFloat(gFormData.lat);
    const lng = parseFloat(gFormData.lng);
    setGSaving(true);
    try {
      const tokens = buildPlaceTokens({
        nameFr: gFormData.nameFr,
        nameAr: gFormData.nameAr,
        nameEn: gFormData.nameEn || '',
        neighborhoodFr: gFormData.neighborhoodFr,
        neighborhoodAr: gFormData.neighborhoodAr,
      });
      const entry = {
        labels: { fr: gFormData.nameFr, ar: gFormData.nameAr || gFormData.nameFr, en: '' },
        neighborhood: { fr: gFormData.neighborhoodFr || '', ar: gFormData.neighborhoodAr || '', en: gFormData.neighborhoodFr || '' },
        geo: { lat, lng },
        googlePlaceId: gSelected?.placeId || '',
        googleMapsUrl: mapsUrl(lat, lng),
        category: gFormData.category || 'مكان',
        searchTokens: tokens,
        aliases: { ar: [], fr: [] },
        stats: { count: 0 },
        verified: true,
        source: 'admin_manual',
        hourlyUsage: new Array(24).fill(0),
        phone: gFormData.phone || '',
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      await push(ref(db, 'sawiPlaces'), entry);
      await update(ref(db, 'sawiPlaces_meta'), { last_modified: Date.now() });
      setToast({ open: true, msg: `"${gFormData.nameFr}" saved to Sawi Places.`, severity: 'success' });
      setGSelected(null);
      setGSearch('');
      setGSuggestions([]);
      setGFormData({ nameFr: '', nameAr: '', lat: '', lng: '', phone: '', neighborhoodFr: '', neighborhoodAr: '', category: '' });
    } catch (e) {
      setToast({ open: true, msg: `Error: ${e.message}`, severity: 'error' });
    } finally {
      setGSaving(false);
    }
  };

  const handleSaveNewPlace = () => {
    if (!gFormData.nameFr || !gFormData.lat || !gFormData.lng) return;
    const lat = parseFloat(gFormData.lat);
    const lng = parseFloat(gFormData.lng);
    const NEARBY = 0.0005;
    const duplicate = places.find(p => {
      if (!p.geo) return false;
      const gpsClose =
        Math.abs((p.geo.lat || 0) - lat) < NEARBY &&
        Math.abs((p.geo.lng || 0) - lng) < NEARBY;
      const placeIdSame = gSelected?.placeId && p.googlePlaceId && p.googlePlaceId === gSelected.placeId;
      return gpsClose || placeIdSame;
    });
    if (duplicate) {
      setDupConfirmDialog({ open: true, dupName: duplicate.labels?.fr || duplicate.labels?.ar || '—' });
      return;
    }
    doSaveNewPlace();
  };

  const sortedPlaces = [...filteredPlaces].sort((a, b) =>
    sortOrder === 'desc' ? (b.createdAt || 0) - (a.createdAt || 0) : (a.createdAt || 0) - (b.createdAt || 0)
  );
  const paginatedPlaces = sortedPlaces.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const sources = Object.keys(stats.bySrc);
  const categories = [...new Set(places.map(p => p.category).filter(Boolean))];

  return (
    <Box className={classes.root}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <PlaceIcon sx={{ color: MAIN_COLOR }} />
        <Typography className={classes.title}>Sawi Places Manager</Typography>
      </Box>

      {/* Toggle */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        {hookLoading ? <CircularProgress size={20} /> : (
          <FormControlLabel
            control={<Switch checked={hookEnabled} onChange={handleToggle} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: MAIN_COLOR }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: MAIN_COLOR } }} />}
            label={<Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Save places after bookings: {hookEnabled ? 'ON' : 'OFF'}</Typography>}
          />
        )}
      </Paper>

      {/* Stats */}
      {!loading && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography className={classes.sectionTitle}>Statistics</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Paper className={classes.statCard} elevation={0} sx={{ border: '1px solid #eee' }}>
                <Typography className={classes.statValue}>{stats.total.toLocaleString()}</Typography>
                <Typography className={classes.statLabel}>Total places</Typography>
              </Paper>
            </Grid>
            {Object.entries(stats.bySrc).map(([src, count]) => (
              <Grid item xs={6} sm={3} key={src}>
                <Paper className={classes.statCard} elevation={0} sx={{ border: '1px solid #eee' }}>
                  <Typography className={classes.statValue}>{count}</Typography>
                  <Typography className={classes.statLabel}>{SOURCE_LABELS[src] || src}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Backfill Google Maps URLs */}
      {!loading && places.some(p => p.geo?.lat && !p.googleMapsUrl) && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #f59e0b', backgroundColor: '#fffbeb' }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: '#92400e' }}>
              {places.filter(p => p.geo?.lat && !p.googleMapsUrl).length} places missing a Google Maps URL.
            </Typography>
            <Button
              variant="contained"
              size="small"
              disabled={backfilling}
              onClick={handleBackfillUrls}
              style={{ backgroundColor: '#f59e0b', fontFamily: FONT_FAMILY, textTransform: 'none', color: '#fff' }}
            >
              {backfilling ? `Backfilling… ${backfillDone} done` : 'Backfill all now'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Add Place from Google */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography className={classes.sectionTitle}>Add Place from Google Maps</Typography>
        <Box sx={{ position: 'relative', display: 'inline-block', width: 300 }}>
          <TextField
            placeholder="Search place name..."
            value={gSearch}
            onChange={e => { setGSearch(e.target.value); setGSelected(null); }}
            size="small"
            fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          {gSuggestions.length > 0 && (
            <Paper elevation={4} sx={{ position: 'absolute', zIndex: 1200, width: '100%', maxHeight: 220, overflowY: 'auto', top: '100%', left: 0 }}>
              {gSuggestions.map(s => (
                <Box key={s.place_id} sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }} onClick={() => handleGoogleSelect(s)}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13 }}>{s.description}</Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Box>
        {gSelected && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Name (FR / EN)" value={gFormData.nameFr} onChange={e => setGFormData(d => ({ ...d, nameFr: e.target.value }))} size="small" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Name (AR)" value={gFormData.nameAr} onChange={e => setGFormData(d => ({ ...d, nameAr: e.target.value }))} size="small" /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth label="Latitude" value={gFormData.lat} onChange={e => setGFormData(d => ({ ...d, lat: e.target.value }))} size="small" /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth label="Longitude" value={gFormData.lng} onChange={e => setGFormData(d => ({ ...d, lng: e.target.value }))} size="small" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Phone" value={gFormData.phone} onChange={e => setGFormData(d => ({ ...d, phone: e.target.value }))} size="small" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Neighborhood (FR — e.g. Nouakchott Nord)" value={gFormData.neighborhoodFr} onChange={e => setGFormData(d => ({ ...d, neighborhoodFr: e.target.value }))} size="small" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Neighborhood (AR)" value={gFormData.neighborhoodAr} onChange={e => setGFormData(d => ({ ...d, neighborhoodAr: e.target.value }))} size="small" /></Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel style={{ fontFamily: FONT_FAMILY }}>Category</InputLabel>
                  <Select value={gFormData.category} label="Category" onChange={e => setGFormData(d => ({ ...d, category: e.target.value }))}>
                    {[...new Set([...categories, 'مستشفى', 'صيدلية', 'مدرسة', 'مسجد', 'مطعم', 'محطة وقود', 'بنك', 'متجر بقالة', 'سوبرماركت', 'عيادة طبية', 'ملعب رياضي', 'مجمع رياضي'])].map(c => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box mt={2} display="flex" gap={1}>
              <Button variant="contained" disabled={gSaving || !gFormData.nameFr || !gFormData.lat} onClick={handleSaveNewPlace} style={{ backgroundColor: MAIN_COLOR, fontFamily: FONT_FAMILY, textTransform: 'none' }}>
                {gSaving ? <CircularProgress size={18} /> : 'Save to Sawi Places'}
              </Button>
              <Button onClick={() => { setGSelected(null); setGSearch(''); setGSuggestions([]); setGFormData({ nameFr: '', nameAr: '', lat: '', lng: '', phone: '', neighborhoodFr: '', neighborhoodAr: '', category: '' }); }} style={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>
                Clear
              </Button>
            </Box>
          </Box>
        )}
        <div ref={placesNodeRef} style={{ display: 'none' }} />
      </Paper>

      {/* Search + Filter */}
      <Box className={classes.filterRow}>
        <TextField
          placeholder="Search places..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          style={{ width: 280, fontFamily: FONT_FAMILY }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <FormControl size="small" style={{ width: 160 }}>
          <InputLabel style={{ fontFamily: FONT_FAMILY }}>Source</InputLabel>
          <Select value={filterSource} label="Source" onChange={e => setFilterSource(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {sources.map(s => <MenuItem key={s} value={s}>{SOURCE_LABELS[s] || s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" style={{ width: 160 }}>
          <InputLabel style={{ fontFamily: FONT_FAMILY }}>Category</InputLabel>
          <Select value={filterCategory} label="Category" onChange={e => setFilterCategory(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        {(search || filterSource || filterCategory) && (
          <Button size="small" onClick={() => { setSearch(''); setFilterSource(''); setFilterCategory(''); }} style={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>
            Clear filters
          </Button>
        )}
      </Box>

      {/* Table */}
      {loading ? (
        <Box textAlign="center" py={6}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>Name</TableCell>
                  <TableCell className={classes.tableHeadCell}>Neighborhood</TableCell>
                  <TableCell className={classes.tableHeadCell}>Category</TableCell>
                  <TableCell className={classes.tableHeadCell}>Visits</TableCell>
                  <TableCell className={classes.tableHeadCell}>Source</TableCell>
                  <TableCell className={classes.tableHeadCell}>GPS</TableCell>
                  <TableCell className={classes.tableHeadCell}>Map</TableCell>
                  <TableCell
                    className={classes.tableHeadCell}
                    onClick={() => { setSortOrder(s => s === 'desc' ? 'asc' : 'desc'); setPage(0); }}
                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                  >
                    Added {sortOrder === 'desc' ? '↓' : '↑'}
                  </TableCell>
                  <TableCell className={classes.tableHeadCell} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedPlaces.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" style={{ fontFamily: FONT_FAMILY, color: '#999', padding: 32 }}>
                      No places found
                    </TableCell>
                  </TableRow>
                ) : paginatedPlaces.map(place => (
                  <TableRow key={place.id} hover>
                    <TableCell style={{ fontFamily: FONT_FAMILY, fontSize: 13 }}>
                      {place.labels?.fr || place.labels?.ar || place.labels?.en || '—'}
                    </TableCell>
                    <TableCell style={{ fontFamily: FONT_FAMILY, fontSize: 13 }}>
                      {(typeof place.neighborhood === 'string' ? place.neighborhood : (place.neighborhood?.fr || place.neighborhood?.ar)) || '—'}
                    </TableCell>
                    <TableCell>
                      {place.category && <Chip label={place.category} size="small" style={{ fontFamily: FONT_FAMILY }} />}
                    </TableCell>
                    <TableCell style={{ fontFamily: FONT_FAMILY, fontSize: 13 }}>{place.stats?.count || place.visitCount || 0}</TableCell>
                    <TableCell>
                      <Chip label={SOURCE_LABELS[place.source] || place.source || '?'} size="small" variant="outlined" style={{ fontFamily: FONT_FAMILY, fontSize: 11 }} />
                    </TableCell>
                    <TableCell style={{ fontFamily: FONT_FAMILY, fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
                      {place.geo?.lat ? `${Number(place.geo.lat).toFixed(4)}, ${Number(place.geo.lng).toFixed(4)}` : '—'}
                    </TableCell>
                    <TableCell>
                      {place.geo?.lat && (
                        <Tooltip title="Open in Google Maps">
                          <IconButton
                            size="small"
                            component="a"
                            href={place.googleMapsUrl || mapsUrl(place.geo.lat, place.geo.lng)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNewIcon fontSize="small" sx={{ color: '#1a73e8' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell style={{ fontFamily: FONT_FAMILY, fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
                      {place.createdAt ? new Date(place.createdAt).toLocaleDateString('fr-FR') : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditOpen(place)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDeleteOpen(place)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredPlaces.length}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
          />
        </Paper>
      )}

      {/* Edit dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontFamily: FONT_FAMILY }}>Edit Place</DialogTitle>
        <DialogContent>
          {[
            { key: 'labels/fr', label: 'Name (FR)' },
            { key: 'labels/ar', label: 'Name (AR)' },
            { key: 'labels/en', label: 'Name (EN)' },
            { key: 'category', label: 'Category' },
            { key: 'phone', label: 'Phone' },
            { key: 'neighborhood/fr', label: 'Neighborhood (FR)' },
            { key: 'neighborhood/ar', label: 'Neighborhood (AR)' },
            { key: 'geo/lat', label: 'GPS Latitude' },
            { key: 'geo/lng', label: 'GPS Longitude' },
          ].map(({ key, label }) => (
            <TextField key={key} fullWidth label={label} value={editData[key] || ''} onChange={e => setEditData(d => ({ ...d, [key]: e.target.value }))} size="small" sx={{ mt: 1.5 }} />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)} style={{ fontFamily: FONT_FAMILY }}>Cancel</Button>
          <Button onClick={handleEditSave} disabled={submitting} variant="contained" style={{ backgroundColor: MAIN_COLOR, fontFamily: FONT_FAMILY }}>
            {submitting ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle style={{ fontFamily: FONT_FAMILY }}>Delete Place</DialogTitle>
        <DialogContent>
          <Typography style={{ fontFamily: FONT_FAMILY }}>
            Delete "{deleteTarget?.labels?.fr || deleteTarget?.labels?.ar || deleteTarget?.id}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} style={{ fontFamily: FONT_FAMILY }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} disabled={submitting} color="error" variant="contained" style={{ fontFamily: FONT_FAMILY }}>
            {submitting ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirmation dialog */}
      <Dialog open={deactivateDialog} onClose={() => setDeactivateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontFamily: FONT_FAMILY }}>Deactivate Place Saving</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Disabling this will stop saving new places from completed bookings. All existing data is preserved.
          </Alert>
          <Typography style={{ fontFamily: FONT_FAMILY }}>
            Do you want to deactivate the "Save places after bookings" feature?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialog(false)} style={{ fontFamily: FONT_FAMILY }}>No, keep active</Button>
          <Button onClick={handleDeactivateConfirm} disabled={submitting} color="error" variant="contained" style={{ fontFamily: FONT_FAMILY }}>
            {submitting ? <CircularProgress size={18} /> : 'Yes, deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate place warning dialog */}
      <Dialog open={dupConfirmDialog.open} onClose={() => setDupConfirmDialog({ open: false, dupName: '' })} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontFamily: FONT_FAMILY }}>Similar Place Already Exists</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            A similar place already exists: <strong>"{dupConfirmDialog.dupName}"</strong>. Are you sure you want to save this new place anyway?
          </Alert>
          <Typography style={{ fontFamily: FONT_FAMILY }}>
            Both places will be kept. Review the existing entry manually if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDupConfirmDialog({ open: false, dupName: '' })} style={{ fontFamily: FONT_FAMILY }}>Cancel</Button>
          <Button
            onClick={() => { setDupConfirmDialog({ open: false, dupName: '' }); doSaveNewPlace(); }}
            color="warning"
            variant="contained"
            style={{ fontFamily: FONT_FAMILY }}
          >
            Continue Saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} style={{ fontFamily: FONT_FAMILY }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
